import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, FolderKanban, CheckSquare, Database, Users, Activity, Settings, LogOut, MessageCircle, Wallet, Home, PanelLeftClose, PanelLeft, Megaphone, Calendar, Bot, Contact, Shield, Mail, Plug, Search } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useUserRole } from '@/hooks/useUserRole';
import { useProfile } from '@/hooks/useProfile';
import { useUnreadMessages } from '@/hooks/useUnreadMessages';
import { useNewItemsIndicator } from '@/hooks/useNewItemsIndicator';
import { useModuleVisibility } from '@/hooks/useModuleVisibility';
import { useIntegrationNotifications } from '@/hooks/useIntegrationNotifications';
import { getRoleLabel, getRoleBadgeColor, ModuleId, ROUTE_MODULES } from '@/lib/rolePermissions';
import { getUserColor, getUserColorStyle } from '@/lib/userColors';
import nexaboardIcon from '@/assets/nexaboard-icon.png';
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarSeparator, SidebarGroup, SidebarGroupLabel, SidebarGroupContent, useSidebar } from '@/components/ui/sidebar';
import { useIsMobile } from '@/hooks/use-mobile';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useEffect, useState, type CSSProperties } from 'react';
import { SearchDialog } from './SearchDialog';

// Badge-Style-Komponente für einheitliche Darstellung
const NavBadge = ({
  count,
  color = 'primary'



}: {count: number;color?: 'primary' | 'green' | 'blue';}) => {
  if (count <= 0) return null;
  const colorClasses = {
    primary: 'border-[hsl(280_100%_70%)] text-[hsl(280_100%_70%)]',
    green: 'border-[hsl(142_70%_55%)] text-[hsl(142_70%_55%)]',
    blue: 'border-[hsl(210_100%_60%)] text-[hsl(210_100%_60%)]'
  };
  return <span className={cn("ml-auto flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[10px] font-bold", "bg-transparent border", colorClasses[color], "group-data-[collapsible=icon]:hidden")} style={{
    borderColor: 'currentColor',
    borderWidth: '1px'
  }}>
      {count > 99 ? '99+' : count}
    </span>;
};

// Komponente für Aufgaben-Badge mit Text
const TaskBadgeText = ({
  tasksCount,
  notesCount



}: {tasksCount: number;notesCount: number;}) => {
  if (tasksCount <= 0 && notesCount <= 0) return null;
  const parts: string[] = [];
  if (tasksCount > 0) {
    parts.push(`${tasksCount} ${tasksCount === 1 ? 'Aufgabe' : 'Aufgaben'}`);
  }
  if (notesCount > 0) {
    parts.push(`${notesCount} ${notesCount === 1 ? 'Bemerkung' : 'Bemerkungen'}`);
  }
  return <span className={cn("ml-auto text-[9px] font-medium text-[hsl(280_100%_70%)] truncate max-w-[90px]", "group-data-[collapsible=icon]:hidden")}>
      {parts.join(', ')}
    </span>;
};
export const DashboardSidebar = () => {
  const location = useLocation();
  const {
    signOut
  } = useAuth();
  const {
    role,
    canAccess,
    getAccessLevel
  } = useUserRole();
  const {
    profile
  } = useProfile();
  const {
    unreadCount
  } = useUnreadMessages();
  const {
    counts,
    markAsViewed,
    markTasksAsViewed
  } = useNewItemsIndicator();
  const {
    state,
    toggleSidebar,
    setOpenMobile
  } = useSidebar();
  const { isModuleVisible } = useModuleVisibility();
  const isMobile = useIsMobile();
  const [searchOpen, setSearchOpen] = useState(false);
  const { getNotificationCount, markIntegrationSeen } = useIntegrationNotifications();

  // Close mobile sidebar on navigation
  const handleMobileClose = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  // Mark items as viewed when navigating to specific pages (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (location.pathname.startsWith('/dashboard/projekte')) {
        markAsViewed('projects');
      } else if (location.pathname.startsWith('/dashboard/aufgaben')) {
        markTasksAsViewed();
      } else if (location.pathname.startsWith('/dashboard/daten')) {
        markAsViewed('files');
        markIntegrationSeen('data');
      } else if (location.pathname.startsWith('/dashboard/team')) {
        markAsViewed('team');
      } else if (location.pathname.startsWith('/dashboard/chat')) {
        markIntegrationSeen('chat');
      } else if (location.pathname.startsWith('/dashboard/kalender')) {
        markIntegrationSeen('calendar');
      } else if (location.pathname.startsWith('/dashboard/marketing')) {
        markIntegrationSeen('marketing');
      } else if (location.pathname.startsWith('/dashboard/kontakte')) {
        markIntegrationSeen('contacts');
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [location.pathname, markAsViewed, markTasksAsViewed, markIntegrationSeen]);
  const handleSignOut = async () => {
    await signOut();
  };
  const getInitials = (name: string | null | undefined) => {
    if (!name || name.trim() === '') return 'U';
    const parts = name.trim().split(' ').filter(Boolean);
    return parts.map((n) => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
  };
  const isActive = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };
  const getBadgeCount = (itemId: string): number => {
    const integrationCount = getNotificationCount(itemId);
    switch (itemId) {
      case 'chat':
        return unreadCount + integrationCount;
      case 'projects':
        return counts.projects + integrationCount;
      case 'data':
        return counts.files + integrationCount;
      default:
        return integrationCount;
    }
  };

  // Check if tasks item needs special badge
  const isTasksItem = (itemId: string) => itemId === 'tasks';
  const getAdminBadgeCount = (itemId: string): number => {
    switch (itemId) {
      case 'team':
        return counts.team;
      default:
        return 0;
    }
  };
  const color = getUserColor(profile?.id || '', profile?.profile_color);
  const initials = getInitials(profile?.full_name);
  const isCollapsed = state === 'collapsed';

  // Navigation items - Hauptnavigation
  const navigationItems: {
    id: string;
    label: string;
    icon: any;
    path: string;
    moduleId: ModuleId;
  }[] = [{
    id: 'overview',
    label: 'Übersicht',
    icon: LayoutDashboard,
    path: '/dashboard',
    moduleId: 'uebersicht'
  }, {
    id: 'projects',
    label: 'Projekte',
    icon: FolderKanban,
    path: '/dashboard/projekte',
    moduleId: 'projekte'
  }, {
    id: 'tasks',
    label: 'Aufgaben',
    icon: CheckSquare,
    path: '/dashboard/aufgaben',
    moduleId: 'aufgaben'
  }, {
    id: 'finanzen',
    label: 'Finanzen',
    icon: Wallet,
    path: '/dashboard/finanzen',
    moduleId: 'finanzen'
  }, {
    id: 'insurance',
    label: 'Versicherungen',
    icon: Shield,
    path: '/dashboard/versicherungen',
    moduleId: 'versicherungen'
  }, {
    id: 'marketing',
    label: 'Marketing',
    icon: Megaphone,
    path: '/dashboard/marketing',
    moduleId: 'marketing'
  }, {
    id: 'data',
    label: 'Daten',
    icon: Database,
    path: '/dashboard/daten',
    moduleId: 'daten'
  }, {
    id: 'calendar',
    label: 'Kalender',
    icon: Calendar,
    path: '/dashboard/kalender',
    moduleId: 'kalender'
  }];

  // Kommunikation items
  const communicationItems: {
    id: string;
    label: string;
    icon: any;
    path: string;
    moduleId: ModuleId;
  }[] = [{
    id: 'chat',
    label: 'Teamchat',
    icon: MessageCircle,
    path: '/dashboard/chat',
    moduleId: 'team_chat'
  }, {
    id: 'email',
    label: 'E-Mail',
    icon: Mail,
    path: '/dashboard/email',
    moduleId: 'email'
  }, {
    id: 'ki-chat',
    label: 'KI-Assistent',
    icon: Bot,
    path: '/dashboard/ki-chat',
    moduleId: 'ki_wissen'
  }, {
    id: 'contacts',
    label: 'Kontakte',
    icon: Contact,
    path: '/dashboard/kontakte',
    moduleId: 'kontakte'
  }];

  // Verwaltung items
  const administrationItems: {
    id: string;
    label: string;
    icon: any;
    path: string;
    moduleId: ModuleId;
  }[] = [{
    id: 'team',
    label: 'Team',
    icon: Users,
    path: '/dashboard/team',
    moduleId: 'team'
  }, {
    id: 'system',
    label: 'System',
    icon: Activity,
    path: '/dashboard/system',
    moduleId: 'system'
  }, {
    id: 'integrations',
    label: 'Integrationen',
    icon: Plug,
    path: '/dashboard/integrationen',
    moduleId: 'integrationen'
  }];

  // Filter items based on user permissions
  const visibleNavigationItems = navigationItems.filter((item) => canAccess(item.moduleId) && isModuleVisible(item.id));
  const visibleCommunicationItems = communicationItems.filter((item) => canAccess(item.moduleId) && isModuleVisible(item.id));
  const visibleAdministrationItems = administrationItems.filter((item) => canAccess(item.moduleId));
  return <Sidebar collapsible="icon" className="border-r border-sidebar-border h-svh">
      <SidebarHeader className="p-2 pb-0 shrink-0">
        {!isCollapsed ?
      <div className="flex items-center">
            <Link to="/dashboard" className="relative shrink-0 flex h-10 w-10 items-center justify-center overflow-visible">
              <img alt="NexaBoard" className="pointer-events-none absolute left-1/2 top-1/2 h-24 w-24 max-w-none -translate-x-1/2 -translate-y-1/2 object-contain" src={nexaboardIcon} />
            </Link>
            <span className="font-bold text-2xl bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500 bg-clip-text text-transparent leading-none truncate flex-1 text-center -ml-8 mr-1">
              {profile?.company_name || 'NexaBoard'}
            </span>
            <div className="shrink-0">
              <Button variant="brand" size="icon" onClick={toggleSidebar} className="h-6 w-6">
                <PanelLeftClose className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div> :

      <div className="flex flex-col items-center py-1">
            <Link to="/dashboard" className="relative flex h-8 w-8 items-center justify-center overflow-visible">
              <img alt="NexaBoard" className="pointer-events-none absolute left-1/2 top-1/2 h-24 w-24 max-w-none -translate-x-1/2 -translate-y-1/2 object-contain" src={nexaboardIcon} />
              <span className="sr-only">Dashboard</span>
            </Link>
            <Button variant="brand" size="icon" onClick={toggleSidebar} className="mt-2 h-6 w-6 shrink-0">
              <PanelLeft className="h-3.5 w-3.5" />
            </Button>
          </div>
      }
      </SidebarHeader>

      <SidebarSeparator className="my-0.5" />

      <SidebarContent className="flex-1 overflow-y-auto overflow-x-hidden flex flex-col justify-evenly min-h-0">
        {/* Navigation */}
        <SidebarGroup className="py-0 px-2">
          <div className="flex items-center justify-between">
            <SidebarGroupLabel className="text-xs py-0 h-5">Navigation</SidebarGroupLabel>
            <button
            onClick={() => setSearchOpen(true)}
            className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors shrink-0 group-data-[collapsible=icon]:hidden"
            title="Suchen">
            
              <Search className="h-3 w-3" />
              <span className="text-xs">Suchen</span>
            </button>
          </div>
          <SidebarGroupContent>
            <SidebarMenu className="gap-0">
              {visibleNavigationItems.map((item) => <SidebarMenuItem key={item.id}>
                  <SidebarMenuButton asChild tooltip={item.label} isActive={isActive(item.path)} className="h-8">
                    <Link to={item.path} onClick={handleMobileClose} className="flex items-center gap-2 text-sidebar-foreground">
                      <item.icon style={{ width: 18, height: 18 }} />
                      <span style={{ fontSize: 15 }}>{item.label}</span>
                      {isTasksItem(item.id) ? <TaskBadgeText tasksCount={counts.tasks} notesCount={counts.taskNotes} /> : <NavBadge count={getBadgeCount(item.id)} color="primary" />}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>)}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Kommunikation */}
        {visibleCommunicationItems.length > 0 && <SidebarGroup className="py-0 px-2">
            <SidebarGroupLabel className="text-xs py-0 h-5">Kommunikation</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu className="gap-0">
                {visibleCommunicationItems.map((item) => <SidebarMenuItem key={item.id}>
                    <SidebarMenuButton asChild tooltip={item.label} isActive={isActive(item.path)} className="h-8">
                      <Link to={item.path} onClick={handleMobileClose} className="flex items-center gap-2 text-sidebar-foreground">
                        <item.icon style={{ width: 18, height: 18 }} />
                        <span style={{ fontSize: 15 }}>{item.label}</span>
                        <NavBadge count={getBadgeCount(item.id)} color="primary" />
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>)}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>}


        {/* Verwaltung */}
        {visibleAdministrationItems.length > 0 && <SidebarGroup className="py-0 px-2">
            <SidebarGroupLabel className="text-xs py-0 h-5">Verwaltung</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu className="gap-0">
                {visibleAdministrationItems.map((item) => <SidebarMenuItem key={item.id}>
                    <SidebarMenuButton asChild tooltip={item.label} isActive={isActive(item.path)} className="h-8">
                      <Link to={item.path} onClick={handleMobileClose} className="flex items-center gap-2 text-sidebar-foreground">
                        <item.icon style={{ width: 18, height: 18 }} />
                        <span style={{ fontSize: 15 }}>{item.label}</span>
                        <NavBadge count={getAdminBadgeCount(item.id)} color="primary" />
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>)}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>}
      </SidebarContent>

      <SidebarFooter className="p-1.5 shrink-0">
        <SidebarSeparator className="mb-1" />

        {/* User Info + Search on same row */}
        <div className="flex items-start gap-2 px-1 group-data-[collapsible=icon]:justify-center">
          {profile?.show_avatar !== false && profile?.avatar_url ?
        <img
          src={profile.avatar_url}
          alt={profile.full_name || 'Avatar'}
          className="h-6 w-6 rounded-full object-cover shrink-0 mt-0.5" /> :


        <div className="h-6 w-6 rounded-full flex items-center justify-center text-[9px] font-bold shrink-0 mt-0.5" style={getUserColorStyle(profile?.id || '', 0.35, profile?.profile_color)}>
              {initials}
            </div>
        }
          <div className="flex-1 min-w-0 group-data-[collapsible=icon]:hidden">
            <div className="flex items-center gap-1.5">
              <p className="text-[15px] font-medium truncate text-foreground leading-tight">
                {profile?.full_name || 'Benutzer'}
              </p>
              {role && <span className="text-[11px] text-muted-foreground leading-tight shrink-0">
                  {getRoleLabel(role)}
                </span>}
            </div>
            <Link
            to="/dashboard/einstellungen"
            onClick={handleMobileClose}
            className={cn(
              "flex items-center gap-1 text-[13px] rounded-md px-1.5 py-0.5 -ml-1.5 mt-0.5 transition-colors whitespace-nowrap text-sidebar-foreground w-fit",
              isActive('/dashboard/einstellungen') ?
              "border border-sidebar-border bg-sidebar-accent text-sidebar-accent-foreground" :
              "hover:bg-sidebar-accent/50"
            )}>
            
              <Settings className="h-3 w-3" />
              Profileinstellungen
            </Link>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center gap-1 px-1 mt-1 group-data-[collapsible=icon]:flex-col">
          <Link
          to="/"
          onClick={handleMobileClose}
          className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1 text-xs text-primary hover:bg-primary/10 rounded transition-colors">
          
            <Home className="h-3.5 w-3.5" />
            <span className="group-data-[collapsible=icon]:hidden">Startseite</span>
          </Link>
          <button
          onClick={handleSignOut}
          className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1 text-xs text-destructive hover:bg-destructive/10 rounded transition-colors">
          
            <LogOut className="h-3.5 w-3.5" />
            <span className="group-data-[collapsible=icon]:hidden whitespace-nowrap">Abmelden</span>
          </button>
        </div>

        <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
      </SidebarFooter>
    </Sidebar>;
};